import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUserRole } from './useUserRole';
import { useUnifiedUser } from './useUnifiedUser';
import { 
  GameFormData, 
  GameFormSubmission, 
  GameSectionPermissions,
  UnifiedGameFormSectionData 
} from '@/types/unifiedGameForm';
import { GameWithDetails } from '@/types/gameSystem';
import { toast } from 'sonner';

interface UseUnifiedGameFormProps {
  gameId: string;
  gameType: 'regular' | 'venue';
}

export const useUnifiedGameForm = ({ gameId, gameType }: UseUnifiedGameFormProps) => {
  const { permissions, userId } = useUserRole();
  const { user: unifiedUser } = useUnifiedUser();
  const [gameData, setGameData] = useState<GameWithDetails | null>(null);
  const [formData, setFormData] = useState<GameFormData | null>(null);
  const [sectionPermissions, setSectionPermissions] = useState<GameSectionPermissions[]>([]);
  const [submissions, setSubmissions] = useState<GameFormSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Get user role for permissions
  const getUserRole = useCallback(() => {
    if (permissions.role === 'admin') return 'admin';
    if (permissions.isVenue) return 'venue';
    if (permissions.isTeamOwner) return 'team_owner';
    if (permissions.isCoach) return 'coach';
    if (permissions.isPlayer) return 'player';
    if (permissions.role === 'scorekeeper') return 'scorekeeper';
    return 'player'; // default
  }, [permissions]);

  // Load game data and form state
  const loadGameData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load game data with team names
      const table = gameType === 'venue' ? 'venue_games' : 'games';
      const { data: game, error: gameError } = await supabase
        .from(table)
        .select(`
          *,
          home_team:teams!home_team_id(team_name),
          away_team:teams!away_team_id(team_name)
        `)
        .eq('id', gameId)
        .maybeSingle();

      if (gameError) {
        console.error('Error loading game:', gameError);
        throw gameError;
      }

      if (!game) {
        console.error('Game not found:', gameId);
        throw new Error(`Game with ID ${gameId} not found. Please check the game code or create a new game.`);
      }

      // Construct game title with team names and home/away designation
      const homeTeamName = game.home_team?.team_name || 'TBD';
      const awayTeamName = game.away_team?.team_name || 'TBD';
      const constructedTitle = `${homeTeamName} (Home) vs ${awayTeamName} (Away)`;
      
      // Add the constructed title and team names to the game data
      (game as any).home_team_name = homeTeamName;
      (game as any).away_team_name = awayTeamName;
      (game as any).constructed_title = constructedTitle;
      
      setGameData(game as unknown as GameWithDetails);

      // Load form data from the game record
      const formData: GameFormData = {
        gameId: game.id,
        gameType,
        gameFormStatus: (game.game_form_status as any) || 'draft',
        formSectionsCompleted: (typeof game.form_sections_completed === 'object' && game.form_sections_completed !== null) ? 
          game.form_sections_completed as any : {
          venue: false,
          teams: false,
          players: false,
          scoring: false
        },
        playerApproved: game.player_approved || false,
        coachApproved: game.coach_approved || false,
        venueApproved: game.venue_approved || false,
        gameCodeAccessLevel: (game.game_code_access_level as any) || 'open'
      };
      setFormData(formData);

      // Load section permissions for current user role
      const userRole = getUserRole();
      const { data: permissions, error: permError } = await supabase
        .from('game_section_permissions')
        .select('*')
        .eq('role_type', userRole);

      if (permError) throw permError;
      setSectionPermissions((permissions || []).map(p => ({
        id: p.id,
        roleType: p.role_type as any,
        sectionName: p.section_name as any,
        canView: p.can_view,
        canEdit: p.can_edit,
        canApprove: p.can_approve,
        conditions: p.conditions as any,
        createdAt: p.created_at,
        updatedAt: p.updated_at
      })));

      // Load existing submissions
      const { data: submissionData, error: subError } = await supabase
        .from('game_form_submissions')
        .select('*')
        .eq('game_id', gameId)
        .eq('game_type', gameType);

      if (subError) throw subError;
      setSubmissions((submissionData || []).map(s => ({
        id: s.id,
        gameId: s.game_id,
        gameType: s.game_type as any,
        userId: s.user_id,
        userType: s.user_type as any,
        userRole: s.user_role as any,
        sectionType: s.section_type as any,
        submissionData: s.submission_data as any,
        submissionStatus: s.submission_status as any,
        approvedBy: s.approved_by,
        approvedAt: s.approved_at,
        rejectionReason: s.rejection_reason,
        createdAt: s.created_at,
        updatedAt: s.updated_at
      })));

    } catch (error) {
      console.error('Error loading game form data:', error);
      toast.error('Failed to load game form data');
    } finally {
      setLoading(false);
    }
  }, [gameId, gameType, getUserRole]);

  // Check if user can access a specific section
  const canAccessSection = useCallback((sectionName: string, accessType: 'view' | 'edit' | 'approve') => {
    const permission = sectionPermissions.find(p => p.sectionName === sectionName);
    
    // Fallback for scorekeeper section: always allow coaches and venues to view/edit
    if (sectionName === 'scorekeeper' && !permission) {
      const userRole = getUserRole();
      if (userRole === 'coach' || userRole === 'venue' || userRole === 'team_owner') {
        console.log('⚠️ No scorekeeper permission found, using fallback for:', userRole);
        if (accessType === 'view' || accessType === 'edit') return true;
        if (accessType === 'approve' && userRole === 'venue') return true;
      }
    }
    
    if (!permission) {
      console.log('❌ No permission found for section:', sectionName, 'access:', accessType);
      return false;
    }

    switch (accessType) {
      case 'view': return permission.canView;
      case 'edit': return permission.canEdit;
      case 'approve': return permission.canApprove;
      default: return false;
    }
  }, [sectionPermissions, getUserRole]);

  // Save section data
  const saveSectionData = useCallback(async (
    sectionType: string, 
    sectionData: UnifiedGameFormSectionData
  ) => {
    if (!userId || !formData) return;

    try {
      setSaving(true);
      
      console.log('💾 Saving section data:', { section: sectionType, gameId, userId: unifiedUser?.id || userId, data: sectionData });
      
      const submission = {
        game_id: gameId,
        game_type: gameType,
        user_id: unifiedUser?.id || userId,
        user_type: unifiedUser?.userType || 'supabase',
        user_role: getUserRole(),
        section_type: sectionType as any,
        submission_data: sectionData as any,
        submission_status: 'pending'
      };

      console.log('💾 Upserting submission:', submission);

      // Use upsert with the unique constraint we added in migration
      const { data: upsertedData, error } = await supabase
        .from('game_form_submissions')
        .upsert(submission, { 
          onConflict: 'game_id,section_type,user_id',
          ignoreDuplicates: false
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Error upserting submission:', error);
        throw new Error(`Failed to save ${sectionType} section: ${error.message}`);
      }
      
      console.log('✅ Submission saved successfully:', upsertedData);

      // Update form sections completed
      const updatedSections = { ...formData.formSectionsCompleted };
      updatedSections[sectionType as keyof typeof updatedSections] = true;

      // Update the game record
      const table = gameType === 'venue' ? 'venue_games' : 'games';
      const gameUpdates: any = {
        form_sections_completed: updatedSections,
        updated_at: new Date().toISOString()
      };

      // If this is venue section data, also update the main game fields
      if (sectionType === 'venue' && sectionData && 'gameDetails' in sectionData) {
        const venueData = sectionData as any;
        if (venueData.gameDetails) {
          gameUpdates.game_date = venueData.gameDetails.gameDate;
          gameUpdates.game_time = venueData.gameDetails.gameTime;
          gameUpdates.game_timezone = venueData.gameDetails.gameTimezone;
          gameUpdates.game_name = venueData.gameDetails.gameName;
          if (venueData.gameDetails.venueAddress) {
            gameUpdates.venue_address = venueData.gameDetails.venueAddress;
          }
        }
      }

      const { error: updateError } = await supabase
        .from(table)
        .update(gameUpdates)
        .eq('id', gameId);

      if (updateError) throw updateError;

      // If this is coach section data, sync game participants for selected players
      if (sectionType === 'coach' && sectionData && 'selectedPlayers' in sectionData) {
        const coachData = sectionData as any;
        if (coachData.selectedPlayers && Array.isArray(coachData.selectedPlayers)) {
          // Get existing participants to preserve their data
          const { data: existingParticipants } = await supabase
            .from('game_participants')
            .select('*')
            .eq('game_id', gameId)
            .eq('requested_by', unifiedUser?.id || userId);

          const existingPlayerIds = new Set(existingParticipants?.map(p => p.player_id) || []);
          const newPlayerIds = new Set(coachData.selectedPlayers.map((p: any) => p.user_id || p.id));

          // Remove participants that are no longer in the roster
          const playersToRemove = Array.from(existingPlayerIds).filter(id => !newPlayerIds.has(id));
          if (playersToRemove.length > 0) {
            console.log('Removing players from roster:', playersToRemove);
            await supabase
              .from('game_participants')
              .delete()
              .eq('game_id', gameId)
              .eq('requested_by', unifiedUser?.id || userId)
              .in('player_id', playersToRemove);
          }

          // Add new participants (only those not already in roster)
          const playersToAdd = coachData.selectedPlayers.filter((p: any) => 
            !existingPlayerIds.has(p.user_id || p.id)
          );

          for (const player of playersToAdd) {
            const participantData = {
              game_id: gameId,
              player_id: player.user_id || player.id,
              player_type: player.user_type || 'supabase',
              team_side: coachData.userTeamSide || (gameData?.home_team_id ? 'home' : 'away'),
              position: player.position || null,
              participation_status: 'confirmed',
              eligibility_status: 'eligible',
              requested_by: unifiedUser?.id || userId
            };

            const { error: participantError } = await supabase
              .from('game_participants')
              .insert(participantData);

            if (participantError) {
              console.error('Error creating game participant:', participantError);
            } else {
              console.log('Added new player to roster:', player.user_id || player.id);
            }
          }
        }
      }

      // Refresh data
      await loadGameData();
      
      toast.success('Section saved successfully');
    } catch (error) {
      console.error('Error saving section data:', error);
      toast.error('Failed to save section data');
    } finally {
      setSaving(false);
    }
  }, [gameId, gameType, userId, formData, getUserRole, loadGameData, unifiedUser, gameData]);

  // Submit for approval
  const submitForApproval = useCallback(async (approverRole: string) => {
    if (!userId || !formData) return;

    try {
      setSaving(true);

      // Create approval hierarchy entry
      const approvalData = {
        game_id: gameId,
        game_type: gameType,
        approval_level: approverRole === 'player' ? 1 : approverRole === 'coach' ? 2 : 3,
        approver_role: approverRole,
        approver_id: unifiedUser?.id || userId,
        approval_status: 'approved',
        approval_data: {},
        approved_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('game_approval_hierarchy')
        .insert(approvalData);

      if (error) throw error;

      // Update game approval status
      const table = gameType === 'venue' ? 'venue_games' : 'games';
      const updateData: any = { updated_at: new Date().toISOString() };
      
      if (approverRole === 'player') updateData.player_approved = true;
      if (approverRole === 'coach') updateData.coach_approved = true;
      if (approverRole === 'venue') updateData.venue_approved = true;

      const { error: updateError } = await supabase
        .from(table)
        .update(updateData)
        .eq('id', gameId);

      if (updateError) throw updateError;

      await loadGameData();
      toast.success(`${approverRole} approval submitted successfully`);
    } catch (error) {
      console.error('Error submitting approval:', error);
      toast.error('Failed to submit approval');
    } finally {
      setSaving(false);
    }
  }, [gameId, gameType, userId, formData, loadGameData, unifiedUser]);

  // Set up real-time subscriptions
  useEffect(() => {
    if (!gameId) return;

    const channel = supabase
      .channel(`unified-game-form-${gameId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'game_form_submissions',
          filter: `game_id=eq.${gameId}`
        },
        () => loadGameData()
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'game_approval_hierarchy',
          filter: `game_id=eq.${gameId}`
        },
        () => loadGameData()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [gameId, loadGameData]);

  // Load data on mount
  useEffect(() => {
    if (gameId) {
      loadGameData();
    }
  }, [gameId, loadGameData]);

  return {
    gameData,
    formData,
    sectionPermissions,
    submissions,
    loading,
    saving,
    canAccessSection,
    saveSectionData,
    submitForApproval,
    refetch: loadGameData,
    userRole: getUserRole()
  };
};